import React, { useState, useEffect, useRef } from "react";
import {
  Language,
  LanguageCode,
  SUICustomerLanguageDropdown,
} from "../../../customers/customerlanguages";
import { FileDrop } from "../../../components/FileDrop";
import { useGetCustomerQuery } from "../../../api/customerApi";
import { EmptyFormInputs } from "../glossaries/EmptyFormInputs";

type TranslationMemoryEntry = {
  id: number;
  source_text: string;
  target_text: string;
};

type TranslationMemoryFormProps = {
  customerLanguages: string;
  sourceLanguage: string;
  targetLanguage: string;
  entries: string;
  formAction: string;
};

export const TranslationMemoryForm: React.FC<TranslationMemoryFormProps> = ({
  customerLanguages,
  sourceLanguage,
  targetLanguage,
  entries,
  formAction,
}) => {
  const { isLoading } = useGetCustomerQuery();
  const languages: Language[] = JSON.parse(customerLanguages);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedSourceLanguage, setSelectedSourceLanguage] = useState<
    Language
  >(JSON.parse(sourceLanguage));

  const [selectedTargetLanguage, setSelectedTargetLanguage] = useState<
    Language
  >(JSON.parse(targetLanguage));

  const [modifiedEntries, setModifiedEntries] = useState<
    TranslationMemoryEntry[]
  >(JSON.parse(entries));

  const handleSourceLanguageChange = (iso: LanguageCode): void => {
    const language = languages.find((language) => language.iso === iso);
    setSelectedSourceLanguage(language);
  };

  const handleTargetLanguageChange = (iso: LanguageCode): void => {
    const language = languages.find((language) => language.iso === iso);
    setSelectedTargetLanguage(language);
  };

  const handleSourceTextChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ): void => {
    const newSourceText = e.target.value;
    setModifiedEntries((prev) => {
      const newEntries = [...prev];
      newEntries[index].source_text = newSourceText;
      return newEntries;
    });
  };

  const handleTargetTextChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ): void => {
    const newTargetText = e.target.value;
    setModifiedEntries((prev) => {
      const newEntries = [...prev];
      newEntries[index].target_text = newTargetText;
      return newEntries;
    });
  };

  const updateUrl = (nextSource: Language, nextTarget: Language): void => {
    const url = new URL(window.location.href);
    const currentSource = url.searchParams.get("source_lang");
    const currentTarget = url.searchParams.get("target_lang");

    const source = nextSource.iso;
    const target = nextTarget.iso;

    let changed = false;
    if (source && source !== currentSource) {
      url.searchParams.set("source_lang", source);
      changed = true;
    }
    if (target && target !== currentTarget) {
      url.searchParams.set("target_lang", target);
      changed = true;
    }

    if (changed) {
      window.history.replaceState({}, "", url.toString());
    }
  };

  const fetchEntries = async (
    sourceLang: string,
    targetLang: string
  ): Promise<void> => {
    try {
      const entriesPath = formAction.replace(/\/$/, "") + "/entries.json";
      const url = new URL(entriesPath, window.location.origin);
      url.searchParams.set("source_lang", sourceLang);
      url.searchParams.set("target_lang", targetLang);

      const response = await fetch(url.toString());
      if (!response.ok) {
        throw new Error("Failed to fetch entries");
      }
      const data = await response.json();
      setModifiedEntries(data.entries);
    } catch (error) {
      console.error("Failed to fetch translation memory entries:", error);
    }
  };

  useEffect(() => {
    if (selectedFile && fileInputRef.current) {
      // Create a new FileList using DataTransfer API
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(selectedFile);
      fileInputRef.current.files = dataTransfer.files;
    } else if (!selectedFile && fileInputRef.current) {
      // Clear the input when file is removed
      fileInputRef.current.value = "";
    }
  }, [selectedFile]);

  useEffect(() => {
    updateUrl(selectedSourceLanguage, selectedTargetLanguage);
    fetchEntries(selectedSourceLanguage.iso, selectedTargetLanguage.iso);
  }, [selectedSourceLanguage, selectedTargetLanguage]);

  return (
    <form
      className="txu-form"
      method="post"
      action={
        formAction +
        `?source_lang=${selectedSourceLanguage.iso}&target_lang=${selectedTargetLanguage.iso}`
      }
      encType="multipart/form-data"
    >
      <header className="tw-flex tw-justify-between">
        <h1 className="tw-flex tw-items-center tw-gap-2">
          <span>Translation Memory:</span>{" "}
          <span>
            {selectedSourceLanguage?.flag} {selectedSourceLanguage?.short_name}
          </span>{" "}
          <i className="material-icons tw-text-gray-500">arrow_forward</i>{" "}
          <span>
            {selectedTargetLanguage?.flag} {selectedTargetLanguage?.short_name}
          </span>
        </h1>
        <button
          type="submit"
          className="txu-btn txu-primary tw-flex tw-items-center txu-icon"
        >
          <i className="material-icons">save</i>Save
        </button>
      </header>

      <details className="txu-accordion">
        <summary className="tw-flex tw-items-center">
          <i className="material-icons tw-text-gray-800 tw-mr-2">description</i>{" "}
          Import Translation Memory from file
        </summary>
        <div className="tw-flex tw-flex-col tw-gap-4">
          <p className="tw-text-gray-500">
            Select a file to upload Translation Memory entries in batch. <br />
            File must be either CSV or Excel (.xlsx). Values are read from the
            first two columns and no header should be included, e.g.:
          </p>
          <table className="txu-table txu-compact txu-border tw-border-t tw-border-gray-200">
            <tbody>
              <tr>
                <td>dress</td>
                <td>Kleid</td>
              </tr>
              <tr>
                <td>baseball cap</td>
                <td>Baseballkappe</td>
              </tr>
              <tr>
                <td>mom-jeans</td>
                <td>mom-jeans</td>
              </tr>
              <tr>
                <td>⋮</td>
                <td>⋮</td>
              </tr>
            </tbody>
          </table>
          <div>
            <FileDrop
              selectedFile={selectedFile}
              isInvalid={false}
              onChange={(file): void => {
                setSelectedFile(file);
              }}
            />
            <input
              ref={fileInputRef}
              type="file"
              name="uploaded_file"
              style={{ display: "none" }}
            />
          </div>
          {modifiedEntries.length > 0 && (
            <label className=" tw-w-full !tw-flex tw-items-center tw-justify-center tw-gap-1">
              <input
                className="txu-input"
                type="checkbox"
                name="merge"
                value="1"
              />
              <span className="tw-uppercase tw-font-medium">
                Merge with existing items (below)
              </span>
            </label>
          )}
        </div>
      </details>

      <div className="txu-divider"></div>

      <article>
        <h3 className="tw-m-0">Translation Memory entries:</h3>
        <p className="tw-text-gray-500 tw-text-sm tw-m-0">
          Add or edit Translation Memory entries below
        </p>
        <div className="tw-grid tw-grid-cols-2 tw-gap-12 tw-my-4">
          <div className="tw-flex tw-items-center tw-gap-4">
            <label className="txu-label !tw-m-0" htmlFor="source_language">
              Source Language:
            </label>
            <SUICustomerLanguageDropdown
              loading={isLoading}
              className="!tw-w-1/2"
              fluid
              disabledLanguageOption={
                selectedTargetLanguage.iso as LanguageCode
              }
              placeholder="Select source language"
              selection
              value={selectedSourceLanguage.iso}
              onChange={(e, { value }): void =>
                handleSourceLanguageChange(value as LanguageCode)
              }
            />
          </div>
          <div className="tw-flex tw-items-center tw-gap-4">
            <label className="txu-label !tw-m-0" htmlFor="target_language">
              Target Language:
            </label>
            <SUICustomerLanguageDropdown
              loading={isLoading}
              className="!tw-w-1/2"
              fluid
              disabledLanguageOption={
                selectedSourceLanguage.iso as LanguageCode
              }
              placeholder="Select target language"
              selection
              value={selectedTargetLanguage.iso}
              onChange={(e, { value }): void =>
                handleTargetLanguageChange(value as LanguageCode)
              }
            />
          </div>
        </div>
        <div className="tw-my-4 tw-text-gray-500 tw-text-lg">
          Results: {modifiedEntries.length} entries found
        </div>
        {modifiedEntries.length > 0 &&
          modifiedEntries.map((entry, index) => (
            <div key={entry.id} className="tw-grid tw-grid-cols-2 tw-gap-12">
              <input
                type="text"
                className="txu-input tw-my-1"
                placeholder="e.g. Jaja!"
                name="sources"
                onChange={(e) => handleSourceTextChange(e, index)}
                value={entry.source_text}
              />
              <input
                type="text"
                className="txu-input tw-my-1"
                placeholder="e.g. Guten Tag!"
                name="targets"
                onChange={(e) => handleTargetTextChange(e, index)}
                value={entry.target_text}
              />
            </div>
          ))}
        <EmptyFormInputs />
      </article>
    </form>
  );
};
